import React, { useState } from 'react';
import { AlertCircle, Bell, LogOut } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert'
import { Button } from './ui/button';

// Define types
type AlertType = 'critical' | 'warning';
type AlertStatus = 'active' | 'resolved';

interface Alert {
  id: string;
  type: AlertType;
  title: string;
  location: string;
  description: string;
  status: AlertStatus;
  timestamp: string;
}

const initialAlerts: Alert[] = [
  {
    id: '1',
    type: 'critical',
    title: 'No connection',
    location: 'Banja Luka - Laus',
    description: 'Connection lost to Locker box',
    status: 'active',
    timestamp: '10.09.2024 12:45:20',
  },
  {
    id: '2',
    type: 'warning',
    title: 'Suspect behaviour detected',
    location: 'Prijedor - Centar',
    description: 'Multiple deposit cancelations',
    status: 'active',
    timestamp: '10.09.2024 13:30:15',
  },
  {
    id: '3',
    type: 'warning',
    title: 'Locker open too long',
    location: 'Banja Luka - Centar',
    description: 'Locker A-123 is open too long',
    status: 'active',
    timestamp: '10.09.2024 14:20:45',
  },
];

const LockerMonitoringApp: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>(initialAlerts);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [resolutionStatus, setResolutionStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const getSystemStatus = (): 'bg-red-500' | 'bg-yellow-500' | 'bg-green-500' => {
    if (alerts.some(alert => alert.type === 'critical' && alert.status === 'active')) {
      return 'bg-red-500';
    } else if (alerts.some(alert => alert.status === 'active')) {
      return 'bg-yellow-500';
    } else {
      return 'bg-green-500';
    }
  };

  const handleAlertClick = (alert: Alert) => {
    setSelectedAlert(alert);
    setResolutionStatus('');
    setError(null);
  };

  const handleResolveAlert = () => {
    if (selectedAlert) {
      if (resolutionStatus.trim() === '') {
        setError('Please enter a resolution status before marking as resolved.');
        return;
      }
      
      const updatedAlerts = alerts.map(alert =>
        alert.id === selectedAlert.id ? { ...alert, status: 'resolved' as AlertStatus } : alert
      );
      setAlerts(updatedAlerts);
      setSelectedAlert(null);
      setResolutionStatus('');
      setError(null);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
    <header className="bg-white shadow-sm p-4 flex justify-between items-center">
      <h1 className="text-2xl font-bold text-gray-800">Lock-R Supervision and Control</h1>
      <Button variant="outline" className="flex items-center">
        <LogOut className="mr-2 h-4 w-4" />
        Logout
      </Button>
    </header>

    <main className="flex-grow p-6 space-y-6 overflow-auto">
      <div className={`${getSystemStatus()} p-4 rounded-lg shadow-md transition-colors duration-300`}>
        <h2 className="text-xl font-semibold text-white">System Status</h2>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-800">
          <Bell className="mr-2 h-5 w-5" />
          Active Alarms
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {alerts.filter(alert => alert.status === 'active').map(alert => (
            <div
              key={alert.id}
              className={`p-4 rounded-lg cursor-pointer transition-transform duration-200 hover:scale-105 ${
                alert.type === 'critical' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
              }`}
              onClick={() => handleAlertClick(alert)}
            >
              <h3 className="font-semibold">{alert.title}</h3>
              <p className="text-sm mt-1">{alert.location}</p>
              <p className="text-sm mt-1">{alert.description}</p>
            </div>
          ))}
        </div>
      </div>

      {selectedAlert && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4 flex items-center text-gray-800">
            <AlertCircle className="mr-2 h-5 w-5" />
            Alert Details
          </h2>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left Column */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-700">{selectedAlert.title}</h3>
              <p className="text-gray-600">Location: {selectedAlert.location}</p>
              <p className="text-gray-600">Alert raised: {selectedAlert.timestamp}</p>
              <p className="mt-4 text-gray-700">{selectedAlert.description}</p>
              {selectedAlert.type === 'critical' && (
                <ul className="list-disc list-inside mt-4 text-gray-700">
                  <li>Locker box may be shut down due to power loss</li>
                  <li>Potential locker box software malfunction</li>
                  <li>Possible communication failure between locker box and central location</li>
                </ul>
              )}
            </div>

            {/* Right Column */}
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700">Action Items:</h4>
                <label className="flex items-center text-gray-700">
                  <input type="checkbox" className="mr-2 rounded" />
                  Verify power status via Video Surveillance
                </label>
                <label className="flex items-center text-gray-700">
                  <input type="checkbox" className="mr-2 rounded" />
                  Attempt remote connection using RDP
                </label>
                <label className="flex items-center text-gray-700">
                  <input type="checkbox" className="mr-2 rounded" />
                  Initiate software restart if necessary
                </label>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Resolution Steps:</h4>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe resolution steps..."
                  rows={4}
                  value={resolutionStatus}
                  onChange={(e) => setResolutionStatus(e.target.value)}
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                className="w-full"
                onClick={handleResolveAlert}
              >
                Mark as Resolved
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  </div>
  );
};

export default LockerMonitoringApp;